import { faTrashCan } from '@fortawesome/pro-light-svg-icons'
import { Fragment, useCallback, useMemo } from 'react'
import { sellDeliveryNoteStatus, sellDeliveryNoteStatusInversed, SellDeliveryNoteStatusKeys } from '@teinor/erp/types/company/order/sell/sellDeliveryNote'
import { useNavigate, useLocation } from 'react-router'
import { ButtonDisabledOrLoader } from '../../../../../../../../../../../baseComponents/Button'
import PermissionChecker from '../../../../../../../../../../../baseComponents/PermissionChecker'
import { TwinIconToolTip } from '../../../../../../../../../../../baseComponents/TwinIcon'
import { OrderData } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import twinFetchPost from '../../../../../../../../../../../utils/globals/data'
import { getBasePath } from '../../../../../../../../../../../utils/globals/link'
import useEditDeleteModal from '../../../../../../../../../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { getUserInfo, getUserPermissions } from '../../../../../../../../../../../utils/reducers/getters'
import { changeErrorMessage } from '../../../../../../../../../../../utils/reducers/reduxDispatch'
import { SellDownloadButtonCSV, SellDownloadButtonPDF, SellDownloadPdfTemplateButton, SellDownloadTags, SellPdfPreview, SellQuickAccessButton, SellViewWorkSheetButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellLayout/SellButtons'
import { checkSellFromOrderAllOk } from '../../../../../../../../../../../utils/globals/order/sell'
import { SellDeliveryNoteDuplicateButton, SellDeliveryNoteRecalculateButton } from '../../../SellDeliveryNButtons'
import ModalDeleteSellDeliveryNoteDraft from './ModalDeleteSellDeliveryNoteDraft'
import { ButtonOrderInfoLeftBase, ButtonOrderInfoLeftBaseProps, OrderDivideIntoChaptersButton, OrderProductTypesButton, OrderVariablesButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'
import { permissionCheck } from '../../../../../../../../../../../baseComponents/PermissionChecker/function'
import ModalSkipOperateStockOrder from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/ModalsOrderTable/ModalSkipOperateStockOrder'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'
import useIsLoading from '../../../../../../../../../../../utils/hooks/useIsLoading'
import { TwinDictionary } from '../../../../../../../../../../../utils/globals/dictionary'

const SellDeliveryNoteCreateStepButtons: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    const { infoMessageQuote } = useSellDeliveryNoteCreateStepButtonsLogic({ data, onSubmit, ...rest })
    const OrderId = data?.header?.id
    return (
        <div className='flex items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useSellDeliveryNoteCreateStepButtonsLogic, ...rest }} className='mr-8' />
            <SellDeliveryNoteProcessButton title={t('processDeliveryNote', 'Procesar albarán')} disabled={infoMessageQuote ? true : false} OrderId={OrderId} onSubmit={onSubmit} status={data?.header.status} infoMessage={infoMessageQuote || ''} />
        </div>
    )
}

const useSellDeliveryNoteCreateStepButtonsLogic = ({ data, onSubmit, userPermissions, orderHeaderURLs, setCompleteData, updateHeaderData }: OrderStepButtonsBaseComponentProps) => {
    const { t } = useTwinTranslation()
    const name = 'sell_delivery_note_create_step_buttons'
    let infoMessageQuote = checkSellFromOrderAllOk(data, 'sellDeliveryNote', t)
    const Order = data?.header
    const userInfo = getUserInfo()
    const EmployeeId = userInfo?.Employee?.id
    const orderEmployeeId = Order?.CreateEmployeeId
    const isMyQuote = orderEmployeeId === EmployeeId
    const comesFromSellOrder = Order?.SellOrderId !== undefined || Order?.SellOrderId !== null
    const hideSellQuickAccessButton = !Order?.QuoteOrderId && !Order?.SellOrderId && !Order?.SellDeliveryNoteId && !Order?.SellInvoiceId && !Order?.WorkSheetId && !Order?.relatedSellOrders?.length && !Order?.relatedSellDeliveryNotes?.length && !Order?.relatedSellInvoices?.length && !Order?.relatedWorkSheets?.length

    const defaultButtons = [{ "id": "previewSellDeliveryNote" }, { "id": "downloadSellDeliveryNote" }, { "id": "quickAccess" }, { "id": "duplicateSell" }, { "id": "deleteSellDeliveryNote" }]

    const sellDeliveryNoteDuplicateText = t('duplicateDeliveryNote', 'Duplicar albarán')
    const sellDeliveryNoteDeleteText = t('deleteDraft', 'Eliminar borrador')
    const sellDeliveryNoteRecalculateText = t('recalculateSellDeliveryNote', 'Recalcular albarán')
    const sellDeliveryNoteDownloadCSVText = t('downloadCsv', 'Descargar csv')
    const sellDeliveryNoteDownloadTagsText = t('printTags', 'Imprimir etiquetas')
    const sellDeliveryNotePreviewPDFText = t('previewPdf', 'Previsualizar PDF')
    const sellDeliveryNoteDownloadPdfTemplateText = t('downloadPdfTemplate', 'Descargar plantilla PDF')
    const sellDeliveryNoteDownloadPdfText = t('downloadPdf', 'Descargar PDF')
    const sellDeliveryNoteViewWorkSheetText = t('goToWorkSheet', 'Ir a la hoja de trabajo')
    const sellDeliveryNoteQuickAccessText = t('quickAccess', 'Acceso rápido')
    const sellDeliveryNoteDivideIntoChaptersText = t('divideIntoChapters', 'Dividir en capítulos')
    const sellDeliveryNoteVariablesText = t('variables', 'Variables')
    const sellDeliveryNoteProductTypesText = t('valueProductTypes', 'Valor tipos de producto')

    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
            return {
                duplicateSell: {
                    component: SellDeliveryNoteDuplicateButton,
                    extraProps: {
                        title: sellDeliveryNoteDuplicateText,
                        id: 'duplicateSell',
                        allRowData: data,
                    }
                },
                deleteSellDeliveryNote: {
                    component: SellDeliveryNoteDeleteButton,
                    showButton: isMyQuote && !comesFromSellOrder,
                    extraProps: {
                        title: sellDeliveryNoteDeleteText,
                        id: 'deleteSellDeliveryNote',
                        allRowData: data,
                    }
                },
                recalculateSellDeliveryNote: {
                    component: SellDeliveryNoteRecalculateButton,
                    extraProps: {
                        title: sellDeliveryNoteRecalculateText,
                        id: 'recalculateSellDeliveryNote',
                        allRowData: data,
                        onSubmit
                    }
                },
                downloadSellCsv: {
                    component: SellDownloadButtonCSV,
                    showButton: !infoMessageQuote,
                    extraProps: {
                        title: sellDeliveryNoteDownloadCSVText,
                        id: 'downloadSellCsv',
                        Order,
                        typeTemplate:'sellDeliveryNote',
                    }
                },
                downloadSellTag: {
                    component: SellDownloadTags,
                    showButton: !infoMessageQuote,
                    extraProps: {
                        title: sellDeliveryNoteDownloadTagsText,
                        id: 'downloadSellTag',
                        Order,
                        typeTemplate:'sellDeliveryNote',
                    }
                },
                previewSellDeliveryNote: {
                    component: SellPdfPreview,
                    showButton: !infoMessageQuote,
                    extraProps: {
                        title: sellDeliveryNotePreviewPDFText,
                        id: 'previewSellDeliveryNote',
                        Order,
                        typeTemplate:'sellDeliveryNote',
                    }
                },
                downLoadSellDeliveryNoteTemplate: {
                    component: SellDownloadPdfTemplateButton,
                    showButton: !infoMessageQuote,
                    extraProps: {
                        title: sellDeliveryNoteDownloadPdfTemplateText,
                        id: 'downLoadSellDeliveryNoteTemplate',
                        Order,
                        key:'downloadSellDeliveryNotePDFTemplate',
                        typeTemplate:'sellDeliveryNote',
                    }
                },
                downloadSellDeliveryNote: {
                    component: SellDownloadButtonPDF,
                    showButton: !infoMessageQuote,
                    extraProps: {
                        title: sellDeliveryNoteDownloadPdfText,
                        id: 'downloadSellDeliveryNote',
                        Order,
                        key:'downloadSellDeliveryNote',
                        typeTemplate:'sellDeliveryNote',
                    }
                },
                viewWorkSheet: {
                    component: SellViewWorkSheetButton,
                    showButton: Order?.WorkSheetId,
                    extraProps: {
                        title: sellDeliveryNoteViewWorkSheetText,
                        id: 'viewWorkSheet',
                        WorkSheetId: Order?.WorkSheetId
                    }
                },
                quickAccess: {
                    component: SellQuickAccessButton,
                    showButton: !hideSellQuickAccessButton,
                    extraProps: {
                        title: sellDeliveryNoteQuickAccessText,
                        id: 'quickAccess',
                        Order
                    }
                },
                divideIntoChaptersQuote: {
                    component: OrderDivideIntoChaptersButton,
                    extraProps: {
                        title: sellDeliveryNoteDivideIntoChaptersText,
                        id: 'divideIntoChaptersQuote',
                        allRowData: data,
                        userPermissions,
                        onSubmit,
                        orderHeaderURLs,
                        setCompleteData,
                        updateHeaderData
                    }
                },
                variablesQuote: {
                    component: OrderVariablesButton,
                    extraProps: {
                        title: sellDeliveryNoteVariablesText,
                        id: 'variablesQuote',
                        allRowData: data,
                        userPermissions,
                        onSubmit,
                        orderHeaderURLs,
                        setCompleteData,
                        updateHeaderData
                    }
                },
                sellProductTypes: {
                    component: OrderProductTypesButton,
                    extraProps: {
                        title: sellDeliveryNoteProductTypesText,
                        id: 'sellProductTypes',
                        allRowData: data,
                        userPermissions,
                        onSubmit,
                        orderHeaderURLs,
                        setCompleteData,
                        updateHeaderData,
                    }
                },
            }
    }, [Order, comesFromSellOrder, data, hideSellQuickAccessButton, infoMessageQuote, isMyQuote, onSubmit, sellDeliveryNoteDeleteText, sellDeliveryNoteDownloadCSVText, sellDeliveryNoteDownloadPdfTemplateText, sellDeliveryNoteDownloadPdfText, sellDeliveryNoteDownloadTagsText, sellDeliveryNoteDuplicateText, sellDeliveryNotePreviewPDFText, sellDeliveryNoteQuickAccessText, sellDeliveryNoteRecalculateText, sellDeliveryNoteViewWorkSheetText, orderHeaderURLs, sellDeliveryNoteDivideIntoChaptersText, sellDeliveryNoteVariablesText, setCompleteData, updateHeaderData, userPermissions, sellDeliveryNoteProductTypesText])

    return { infoMessageQuote, ButtonComponents, name, defaultButtons  }
}

export const SellDeliveryNoteCreateStepButtonsNew: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    const { infoMessageQuote } = useSellDeliveryNoteCreateStepButtonsLogic({ data, onSubmit, ...rest })
    const OrderId = data?.header?.id
    return (
        <div className='flex order_config_buttons_parent items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useSellDeliveryNoteCreateStepButtonsLogic, ...rest }} className='mr-8' />
            <SellDeliveryNoteProcessButton title={t('processDeliveryNote', 'Procesar albarán')} disabled={infoMessageQuote ? true : false} OrderId={OrderId} onSubmit={onSubmit} status={data?.header.status} infoMessage={infoMessageQuote || ''} />
        </div>
    )
}

type SellDeliveryNoteProcessButtonProps = SellDeliveryNoteProcessButtonLogicProps & ButtonOrderInfoLeftBaseProps & {
    title: string
    children?: React.ReactNode
}

const SellDeliveryNoteProcessButton: React.FC<SellDeliveryNoteProcessButtonProps> = ({ title, OrderId, onSubmit, status, disabled, infoMessage, ...rest }) => {
    const { finalizeOrder, openModal, setOpenModal, loading} = useSellDeliveryNoteProcessButtonLogic({ OrderId, onSubmit, status })
    return (
        <Fragment>
            <ButtonOrderInfoLeftBase disabled={disabled} infoMessage={infoMessage} {...rest}>
                <div className='min-w-175'>
                    <ButtonDisabledOrLoader onClick={() => finalizeOrder('/api/app/order/sell/sellDeliveryNote/updateSellDeliveryNote')} buttonIsDisabled={disabled} textButton={title} loading={loading} className='w-full' />
                </div>
            </ButtonOrderInfoLeftBase>
            {openModal?.type === 'edit' ? <ModalSkipOperateStockOrder checkStockURLs={'/api/app/order/sell/sellDeliveryNote/checkAllSellDeliveryNoteProductsStock'} setOpenModal={setOpenModal} allRowData={{...openModal.allRowData, createUrl: '/api/app/order/sell/sellDeliveryNote/updateSellDeliveryNote'}} finalizeOrder={finalizeOrder} /> : null}
        </Fragment>
    )
}

interface SellDeliveryNoteProcessButtonLogicProps {
    onSubmit: () => void
    OrderId?: string
    status: number
}

const useSellDeliveryNoteProcessButtonLogic = ({ onSubmit, OrderId, status }: SellDeliveryNoteProcessButtonLogicProps) => {
    const { t } = useTwinTranslation()
    const { openModal, setOpenModal } = useEditDeleteModal()
    const errorMsg = t('noProductStockOnLine', 'No hay stock del producto en la línea')
    const { startLoading, endLoading, loading } = useIsLoading()

    const finalizeOrder = useCallback(async (url: string, navigateUrl?: string, extraProps?: TwinDictionary) => {
        startLoading?.()
        const nextStatus = sellDeliveryNoteStatus[status as SellDeliveryNoteStatusKeys] === 'waitingResponse' ? sellDeliveryNoteStatusInversed['accepted'] : sellDeliveryNoteStatusInversed['waitingResponse']
        const response = await twinFetchPost(url, { id: OrderId, status: nextStatus, ...extraProps })
        const responseJSON = await response.json()
        if (response.status === 200) {
            onSubmit()
        } else if (responseJSON.errors[0].msg.includes('noStock')) {
            const skipStock = permissionCheck(getUserPermissions('sellOther.sellWithoutStock'), 'read')
            if (skipStock) {
                setOpenModal({ type: 'edit', allRowData: { extraProps: { OrderId, status: nextStatus, id: OrderId }, createUrl: url}})
            } else {
                const msgArr = responseJSON.errors[0].msg.split('-')
                let myMsg = errorMsg + ' ' + msgArr[1]
                if (msgArr[2]) {
                    myMsg += ' nº: ' + msgArr[2]
                }
                changeErrorMessage(myMsg)
            }
        }
        endLoading?.()
    }, [OrderId, onSubmit, status, errorMsg, setOpenModal, startLoading, endLoading,])

    return { finalizeOrder, openModal, setOpenModal, loading}
}

interface SellDeliveryNoteDeleteButtonProps {
    allRowData: OrderData
    title: string
    id: string
    children?: React.ReactNode
}

const SellDeliveryNoteDeleteButton: React.FC<SellDeliveryNoteDeleteButtonProps> = ({ allRowData, id, title, children }) => {
    const { openModal, setOpenModal, myOnSubmit } = useSellDeliveryNoteDeleteButtonLogic()
    return (
        <PermissionChecker permission='delete' userPermissions={getUserPermissions('sell.deliveryNote')}>
            <div onClick={() => setOpenModal({ type: 'delete', allRowData })}>
                {children ?? <TwinIconToolTip classNameIcon='hover:text-red-BA regular22' message={title} icon={faTrashCan} id={id} />}
            </div>
            {openModal?.type === 'delete' ? <ModalDeleteSellDeliveryNoteDraft setOpened={() => setOpenModal(null)} allRowData={allRowData} onSubmit={myOnSubmit} /> : null}
        </PermissionChecker>
    )
}

const useSellDeliveryNoteDeleteButtonLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const navigate = useNavigate()
    const location = useLocation()
    const basePath = getBasePath(location.pathname, 'sellDeliveryNotes')

    const myOnSubmit = useCallback(() => {
        navigate(basePath)
    }, [navigate, basePath])
    return { openModal, setOpenModal, myOnSubmit }
}

export default SellDeliveryNoteCreateStepButtons